// The signed-in context every screen reads: who, which business, role,
// capabilities, flags, operating mode, terminology. Built from GET /api/erp/me
// (composed with settings + memberships, see lib/me.ts) and the flags source
// for the mode:
//   online → bff-business GET /api/business/v1/config `flags`, overlaid with
//            the tenant-effective GET /api/business/v1/business/features
//            (features win key by key — lib/features.ts mergeFlags)
//   node   → /me `flags` (erp-core: from the licence; {} while unlicensed)
// Flags fail CLOSED: a source that fails to load yields an empty map (every
// gated module absent). A features failure does not block the shell (config
// still decides maintenance/theme/…): every erp.* reads OFF and the shell
// shows an inline notice with a retry (`featuresFailed` / `retryFeatures`).
//
// Session bootstrap states handled here (before the shell exists):
//   - /me 401 session_locked (a reload of a locked page) → the lock screen,
//     not an error card; unlocking refetches /me.
//   - scheduled maintenance (config `maintenance` / any 503 maintenance) →
//     redirect to /maintenance (online only; a node has no cloud).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BUILD_MODE, type ErpMode } from '@/lib/env';
import { isApiError } from '@/lib/api';
import { sessionStore, setRuntimeMode } from '@/lib/auth';
import { useConfigQuery } from '@/lib/config';
import { mergeFlags, useFeaturesQuery } from '@/lib/features';
import { lockStore } from '@/lib/idle-lock';
import { maintenancePath, useMaintenance } from '@/lib/maintenance';
import { hasCapability, useMeQuery, webMode, type Capability, type Me, type Role } from '@/lib/me';
import { makeT, termProfile, type TermKey, type TermProfile } from '@/lib/terminology';
import type { GateContext } from './gates';
import { LockScreen, useLocked } from './LockScreen';
import { useMaintenancePoll } from './Maintenance';

export interface ErpContextValue {
  me: Me;
  role: Role;
  flags: Record<string, boolean>;
  mode: ErpMode;
  termProfile: TermProfile;
  /** Terminology: t('customer') → المراجع / الزبون. */
  t: (key: TermKey) => string;
  gateContext: GateContext;
  can: (cap: Capability | string) => boolean;
  flag: (name: string) => boolean;
  /** Switch the active business (X-Business-Id) and refetch everything. */
  switchBusiness: (businessId: string) => void;
  /**
   * Switch the active branch (X-Branch-Id on every erp-core call) and refetch
   * everything. Only meaningful with `erp.branches`; null = the main branch.
   */
  switchBranch: (branchId: string | null) => void;
  /** Online: GET /v1/business/features failed — erp.* read OFF; the shell shows a retry notice. */
  featuresFailed: boolean;
  /** Ask for the tenant features again (the notice's retry). */
  retryFeatures: () => void;
}

const Ctx = createContext<ErpContextValue | null>(null);

export function useErp(): ErpContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useErp must be used inside <ErpProvider>');
  return v;
}

/** Nullable variant for components that also render outside the shell (login). */
export function useErpOptional(): ErpContextValue | null {
  return useContext(Ctx);
}

/** Terminology hook usable anywhere (falls back to default terms outside the shell). */
export function useT(): (key: TermKey) => string {
  const v = useContext(Ctx);
  return v?.t ?? makeT('default');
}

export function buildErpValue(
  me: Me,
  flags: Record<string, boolean>,
  switchBusiness: (id: string) => void,
  switchBranch: (id: string | null) => void = () => {},
  features: { failed: boolean; retry: () => void } = { failed: false, retry: () => {} },
): ErpContextValue {
  const mode = webMode(me, BUILD_MODE);
  const profile = termProfile(me.business.sector, me.business.country);
  const gateContext: GateContext = { flags, role: me.role, capabilities: me.capabilities ?? [], mode, sector: me.business.sector ?? null };
  return {
    me,
    role: me.role,
    flags,
    mode,
    termProfile: profile,
    t: makeT(profile),
    gateContext,
    can: (cap) => hasCapability(me, cap),
    flag: (name) => flags[name] === true,
    switchBusiness,
    switchBranch,
    featuresFailed: features.failed,
    retryFeatures: features.retry,
  };
}

/**
 * The flags for the session's mode: node → /me flags (licence); online → BFF
 * config overlaid with the tenant features (features win; no features ⇒ erp.* off).
 */
export function flagsFor(
  me: Pick<Me, 'mode' | 'flags'> | undefined,
  configFlags: Record<string, boolean> | undefined,
  features?: Record<string, boolean>,
): Record<string, boolean> {
  if (!me) return {};
  if (me.mode === 'node') return { ...(me.flags ?? {}) };
  return mergeFlags(configFlags, features);
}

/** Test/preview provider with a fixed value. */
export function ErpContextProvider({ value, children }: { value: ErpContextValue; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export interface ErpProviderRenderProps {
  loading: boolean;
  error: unknown;
  retry: () => void;
}

/**
 * Loads me + config for the signed-in session and provides the context.
 * `fallback` renders while loading, or with the error when /me or /config
 * failed (config failure fails closed — nothing gated renders).
 */
/** /me refused because the server locked the session for idleness (node: erp-core `session_locked`). */
export function isSessionLocked(e: unknown): boolean {
  return isApiError(e) && e.status === 401 && e.code === 'session_locked';
}

export function ErpProvider({ children, fallback }: { children: ReactNode; fallback: (p: ErpProviderRenderProps) => ReactNode }) {
  const qc = useQueryClient();
  const meQ = useMeQuery();
  const node = meQ.data?.mode === 'node';
  // Online flags come from the BFF config; a node's come with /me (licence).
  const cfgQ = useConfigQuery(meQ.isSuccess && !node);
  // …overlaid with the tenant-effective features (online only).
  const featQ = useFeaturesQuery(meQ.isSuccess && !node);
  const locked = useLocked();
  const maint = useMaintenance();
  const { pathname, search } = useLocation();
  const meLocked = meQ.isError && isSessionLocked(meQ.error);
  // Working offline through a maintenance window: keep asking whether it ended
  // (the store then brings the app back online — lib/maintenance.ts).
  useMaintenancePoll(maint.workingOffline && !node, () => {});

  // Adopt the server's active business (first load) and operating mode.
  useEffect(() => {
    if (!meQ.data) return;
    if (!sessionStore.getBusinessId()) sessionStore.setBusinessId(meQ.data.business.id);
    setRuntimeMode(webMode(meQ.data, BUILD_MODE));
  }, [meQ.data]);

  // A reload of a locked page: /me answers 401 session_locked. Show the lock
  // screen (api.ts already locked the store; make sure) and load /me again
  // once the PIN unlocks the same session.
  // Lock once per refusal (not again after the PIN lifted it while /me reloads).
  const lockedFor = useRef(0);
  useEffect(() => {
    if (!meLocked || lockedFor.current === meQ.errorUpdatedAt) return;
    lockedFor.current = meQ.errorUpdatedAt;
    if (!lockStore.isLocked()) lockStore.lock('idle');
  }, [meLocked, meQ.errorUpdatedAt]);
  const refetchMe = meQ.refetch;
  const wasLocked = useRef(locked);
  useEffect(() => {
    const lifted = wasLocked.current && !locked;
    wasLocked.current = locked;
    if (lifted && meLocked) void refetchMe();
  }, [locked, meLocked, refetchMe]);

  const switchBusiness = useCallback(
    (id: string) => {
      sessionStore.setBusinessId(id);
      void qc.resetQueries();
    },
    [qc],
  );

  const switchBranch = useCallback(
    (id: string | null) => {
      if (id === sessionStore.getBranchId()) return;
      sessionStore.setBranchId(id);
      void qc.resetQueries();
    },
    [qc],
  );

  // switchBusiness/switchBranch reset EVERY query (resetQueries without a
  // filter), FEATURES_QUERY_KEY included: the new business's features load.
  const flags = useMemo(() => flagsFor(meQ.data, cfgQ.data?.flags, featQ.data?.features), [meQ.data, cfgQ.data, featQ.data]);
  // A failed refetch keeps the last good answer (react-query keeps `data`): only fail closed with nothing in hand.
  const featuresFailed = !node && featQ.isError && !featQ.data;
  const refetchFeatures = featQ.refetch;
  const retryFeatures = useCallback(() => void refetchFeatures(), [refetchFeatures]);
  const value = useMemo(
    () => (meQ.data ? buildErpValue(meQ.data, flags, switchBusiness, switchBranch, { failed: featuresFailed, retry: retryFeatures }) : null),
    [meQ.data, flags, switchBusiness, switchBranch, featuresFailed, retryFeatures],
  );

  // Scheduled maintenance (online only): the platform window, or any 503
  // maintenance answer (even for /me or /config themselves).
  if (maint.blocks && !node) {
    const back = `${pathname}${search}`;
    const to = maintenancePath(maint.window);
    return <Navigate to={`${to}${to.includes('?') ? '&' : '?'}from=${encodeURIComponent(back)}`} replace />;
  }

  if (meLocked) {
    return (
      <>
        {fallback({ loading: true, error: null, retry: () => void meQ.refetch() })}
        <LockScreen />
      </>
    );
  }
  if (meQ.isError) return <>{fallback({ loading: false, error: meQ.error, retry: () => void meQ.refetch() })}</>;
  // Config failure fails CLOSED: no module renders; the error card offers a retry.
  if (!node && cfgQ.isError) return <>{fallback({ loading: false, error: cfgQ.error, retry: () => void cfgQ.refetch() })}</>;
  // Features: wait until they settle (no flash of absent modules / a Forbidden
  // deep link) — but an error does NOT block: the shell renders with erp.* off.
  if (!value || (!node && (!cfgQ.isSuccess || featQ.isPending))) return <>{fallback({ loading: true, error: null, retry: () => void meQ.refetch() })}</>;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
